import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, ArrowUpRight } from "lucide-react";
import { SERVICES } from "@/site.config";
import QuoteForm from "@/components/QuoteForm";
import Seo from "@/components/Seo";

const SERVICE_SLUG_MAP = {
  gouttieres: "nettoyage-gouttieres-martinique",
  facades: "lessivage-sols-martinique",
  photoluminescent: "nez-de-marches-photoluminescents-martinique",
  "plots-solaires": "plots-solaires-led-martinique",
  depoussierage: "depoussierage-hauteur-martinique",
};

export default function ServicesPage() {
  const navigate = useNavigate();
  return (
    <main data-testid="services-page" className="pt-32">
      <Seo
        title="Nos services — Nettoyage technique, lessivage, photoluminescent & plots solaires LED | SIGNA CLEAN Martinique"
        description="Catalogue complet : nettoyage de gouttières par aspiration 12 m, nettoyage de façades, toitures et bardages, lessivage de sols, équipements photoluminescents (nez de marches, signalisation évacuation), plots solaires LED pour voiries en Martinique."
        keywords="services nettoyage Martinique, nettoyage gouttières aspiration, lessivage sol Martinique, perche télescopique nettoyage, photoluminescent nez de marches, plots solaires LED voirie, signalisation routière Martinique, bardage métallique nettoyage"
        canonical="https://signaclean.fr/services"
      />
      <section className="relative px-5 sm:px-8 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#9DEB30] mb-3">
            · Catalogue 2025
          </div>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight max-w-4xl">
            Solutions techniques{" "}
            <span className="text-[#9DEB30] neon-text">pour bâtiments & voiries.</span>
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl">
            De l'aspiration de gouttières aux plots solaires LED, découvrez l'ensemble
            de nos prestations spécialisées en Martinique.
          </p>
        </div>
      </section>

      <section className="px-5 sm:px-8 pb-24 space-y-8">
        <div className="max-w-7xl mx-auto space-y-8">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const reverse = i % 2 === 1;
            const slug = SERVICE_SLUG_MAP[s.id];
            const goToDetail = () => slug && navigate(`/${slug}`);
            return (
              <article
                key={s.id}
                id={s.id}
                data-testid={`service-detail-${s.id}`}
                onClick={goToDetail}
                role={slug ? "link" : undefined}
                tabIndex={slug ? 0 : -1}
                onKeyDown={(e) => {
                  if (slug && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    goToDetail();
                  }
                }}
                className={`group grid lg:grid-cols-2 gap-8 items-stretch rounded-3xl overflow-hidden border border-white/10 bg-[#0d0d0d] transition ${slug ? "cursor-pointer hover:border-[#9DEB30]/40 focus:outline-none focus:ring-2 focus:ring-[#9DEB30]/60" : ""}`}
              >
                <div className={`relative aspect-[4/3] lg:aspect-auto overflow-hidden ${reverse ? "lg:order-2" : ""}`}>
                  <img
                    src={s.image}
                    alt={s.title}
                    style={{ objectPosition: s.imagePosition || "center" }}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/20 to-transparent" />
                  <div className="absolute top-5 left-5 px-3 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-wider bg-black/70 border border-[#9DEB30]/30 text-[#9DEB30]">
                    {s.tag}
                  </div>
                </div>
                <div className="p-8 sm:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-[#9DEB30]/10 border border-[#9DEB30]/30 text-[#9DEB30]">
                      <Icon size={20} />
                    </div>
                    <span className="text-xs font-mono text-zinc-500">Service · 0{i + 1}</span>
                  </div>
                  <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{s.title}</h2>
                  <p className="mt-4 text-zinc-400">{s.short}</p>
                  <ul className="mt-6 space-y-2">
                    {s.bullets.map((b, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-zinc-200">
                        <Check size={14} className="text-[#9DEB30] mt-1 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex flex-wrap items-center gap-4">
                    {slug && (
                      <span
                        data-testid={`service-detail-learn-${s.id}`}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-white group-hover:text-[#9DEB30]"
                      >
                        En savoir plus
                        <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    )}
                    <Link
                      to="/contact"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`service-detail-cta-${s.id}`}
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-full btn-neon text-sm"
                    >
                      Demander un devis
                      <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <QuoteForm />
    </main>
  );
}
